import { format, addDays } from 'date-fns';
import type { IDatePicker } from '@bridebook/models/source/models/Weddings.types';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import { CountryCodes, Market } from '@bridebook/toolbox/src/gazetteer';

type Params = {
  weddingDate?: IDatePicker | null;
  city?: string;
  latitude?: number;
  longitude?: number;
  country?: CountryCodes;
  market?: Market;
};

const ALLOWED_LANGUAGES = ['en', 'fr', 'de'];

/**
 * Build a HotelPlanner search URL using venue and wedding date.
 * - Venue params (City/latitude/longitude) are included only when the supplier exists and is not custom.
 * - CheckInDate1 is the wedding date; OutDate is +1 day; both have display variants.
 * - Rooms is always 1.
 */
export const buildHotelPlannerUrl = ({
  weddingDate,
  city,
  latitude,
  longitude,
  country,
  market,
}: Params): string => {
  const language = market?.language || 'en';
  const baseUrl = `https://${language != 'en' && ALLOWED_LANGUAGES.includes(language) ? `${language}.` : ''}bridebook.hotelplanner.com/Search/`;
  const qs = new URLSearchParams();

  // Rooms: fixed to 1
  qs.set('Rooms', '1');

  const includeDates = !!weddingDate && isDateExact(weddingDate);

  if (includeDates) {
    const { y, m, d } = weddingDate as IDatePicker;
    const checkIn = new Date(Number(y), Number(m), Number(d));
    const checkOut = addDays(checkIn, 1);

    // MM/DD/YYYY
    qs.set('CheckInDate1', format(checkIn, 'MM/dd/yyyy'));
    qs.set('OutDate', format(checkOut, 'MM/dd/yyyy'));

    // DD/MM/YYYY (display variants)
    qs.set('CheckInDate1_disp', format(checkIn, 'dd/MM/yyyy'));
    qs.set('OutDate_disp', format(checkOut, 'dd/MM/yyyy'));
  }

  // Venue location params (optional)
  // passing city if latitude and longitude are available makes the search less precise
  if (city && (typeof latitude !== 'number' || typeof longitude !== 'number'))
    qs.set('City', `${city}, ${country ?? ''}`);
  if (typeof latitude === 'number') qs.set('latitude', String(latitude));
  if (typeof longitude === 'number') qs.set('longitude', String(longitude));

  const query = qs.toString();
  return `${baseUrl}${query ? `?${query}` : ''}`;
};

export default buildHotelPlannerUrl;
